/**
 * Controlla se il browser permette di far partire un video CON l'audio senza un gesto sulla pagina.
 * Succede quando l'utente ha gia' interagito con il sito: per esempio arriva qui cliccando un
 * contenuto della home o della barra laterale (Chrome conserva l'attivazione nelle navigazioni
 * same-origin), oppure ha gia' consentito l'autoplay al sito.
 * Non faccio partire il contenuto per provarci: prima dello start vero devono essere pronti il
 * pre-roll e lo stato del player, quindi uso l'API del browser o un audio muto generato al volo.
 */
// Oltre questo tempo considero l'autoplay non permesso e aspetto il tap dell'utente
const PROBE_TIMEOUT_MS = 1500;
let autoplayWithSoundCheck = null;
/**
 * True se il video puo' partire con l'audio senza aspettare un tap. Calcolato una volta per pagina.
 */
export function canAutoplayWithSound() {
    if (autoplayWithSoundCheck === null) {
        autoplayWithSoundCheck = detectAutoplayWithSound();
    }
    return autoplayWithSoundCheck;
}
async function detectAutoplayWithSound() {
    const browserNavigator = navigator;
    // API ufficiale (per ora solo Firefox): "allowed" vuol dire con audio, "allowed-muted" solo muto
    if (typeof browserNavigator.getAutoplayPolicy === "function") {
        try {
            return browserNavigator.getAutoplayPolicy("mediaelement") === "allowed";
        }
        catch {
            // API presente ma non utilizzabile: passo alla prova con l'audio
        }
    }
    let sourceUrl = "";
    const probe = new Audio();
    try {
        sourceUrl = createSilentWavUrl();
        probe.muted = false;
        probe.src = sourceUrl;
        const playPromise = probe.play();
        // browser senza promise su play(): non posso saperlo, aspetto il tap
        if (!playPromise) {
            return false;
        }
        await Promise.race([
            playPromise,
            new Promise((_resolve, reject) => setTimeout(() => reject(new Error("timeout")), PROBE_TIMEOUT_MS)),
        ]);
        return true;
    }
    catch {
        // NotAllowedError: serve un gesto dell'utente
        return false;
    }
    finally {
        probe.pause();
        probe.removeAttribute("src");
        probe.load();
        if (sourceUrl !== "") {
            URL.revokeObjectURL(sourceUrl);
        }
    }
}
/**
 * WAV di 0,1 secondi di silenzio generato in memoria, cosi' la prova non scarica nulla
 */
function createSilentWavUrl() {
    const sampleRate = 8000;
    const samples = 800;
    const buffer = new ArrayBuffer(44 + samples);
    const view = new DataView(buffer);
    const writeText = (offset, text) => {
        for (let i = 0; i < text.length; i++) {
            view.setUint8(offset + i, text.charCodeAt(i));
        }
    };
    // header WAV PCM mono a 8 bit
    writeText(0, "RIFF");
    view.setUint32(4, 36 + samples, true);
    writeText(8, "WAVE");
    writeText(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate, true);
    view.setUint16(32, 1, true);
    view.setUint16(34, 8, true);
    writeText(36, "data");
    view.setUint32(40, samples, true);
    // a 8 bit il silenzio e' 128
    for (let i = 0; i < samples; i++) {
        view.setUint8(44 + i, 128);
    }
    return URL.createObjectURL(new Blob([buffer], { type: "audio/wav" }));
}
